import React, { useCallback, useEffect, useState } from "react"
import { Collapse, Descriptions, Tag, Row, Col, Image, Spin } from "antd"
import { FileTextOutlined, ToolOutlined, DashboardOutlined, InfoCircleOutlined, CameraOutlined, EnvironmentOutlined, CheckCircleOutlined } from "@ant-design/icons"
import { IHeTimesheet } from "@/types"
import { useHeTimesheetStore } from "@/stores/he-timesheets/heTimesheetStore"
import { useEquipmentsStore, useUserStore } from "@/stores"

const { Panel } = Collapse

interface Props {
  data: IHeTimesheet
}

const DetailSection: React.FC<{ title: React.ReactNode; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => {
  return (
    <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
      <Panel
        header={
          <span>
            {icon} <span style={{ marginLeft: 8 }}>{title}</span>
          </span>
        }
        key="1"
      >
        {children}
      </Panel>
    </Collapse>
  )
}

const AttachmentItem: React.FC<{ fileKey: string; label: string }> = ({ fileKey, label }) => {
  const fetchHeTimesheetAttachmentUrl = useHeTimesheetStore(state => state.fetchHeTimesheetAttachmentUrl)
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const getUrl = async () => {
      setLoading(true)
      try {
        const res = await fetchHeTimesheetAttachmentUrl({ key: fileKey })
        setUrl(res)
      } catch (error) {
        console.error("Failed to fetch attachment URL", error)
      } finally {
        setLoading(false)
      }
    }

    if (fileKey) {
      void getUrl()
    }
  }, [fileKey, fetchHeTimesheetAttachmentUrl])

  if (loading) return <Spin size="small" />
  if (!url) return <span>No Image</span>

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
      <span style={{ fontWeight: 600 }}>{label}:</span>
      <Image width={150} src={url} alt={label} style={{ borderRadius: 8, objectFit: "cover" }} />
    </div>
  )
}

const HeTimesheetDetailView: React.FC<Props> = ({ data }) => {
  const { userData } = useUserStore()
  const { equipments, fetchEquipments } = useEquipmentsStore()
  const isAdmin = userData?.roles?.includes("ROLE_ADMIN")
  const [isDrilling, setIsDrilling] = useState(false)
  const depthMeterDelta =
    data?.depth_meter_from !== undefined && data?.depth_meter_to !== undefined
      ? Number((data.depth_meter_to - data.depth_meter_from).toFixed(2))
      : undefined

  const assignmentType = data?.assignment_type || data?.type_assignment
  const blockCode = data?.block || data?.block_type
  const blockDesc = data?.block_description || data?.block_desc
  const prospectCode = data?.prospect || data?.prospect_type
  const locationName = data?.location || data?.location_timesheet || data?.job_location
  const hasAssignmentInfo = Boolean(assignmentType || blockCode || blockDesc || prospectCode || data?.prospect_desc || locationName || data?.location_type || data?.location_timesheet)
  const unitType = data?.unit_type || data?.hull_name
  const unitCode = data?.unit_code || data?.hull_code
  const totalHours = data?.total_hours ?? data?.hm_total
  const approval = data?.approval
  const hasApprovalInfo = Boolean(approval && (approval.name || approval.nik || approval.email))

  useEffect(() => {
    const resolveEquipmentCategory = async () => {
      if (!unitCode) {
        setIsDrilling(false)
        return
      }

      try {
        await fetchEquipments({ page: 1, limit: 10, search: unitCode })
      } catch (error) {
        console.error("Failed to fetch equipments for drilling check", error)
      }
    }

    void resolveEquipmentCategory()
  }, [unitCode, fetchEquipments])

  useEffect(() => {
    if (!unitCode) {
      setIsDrilling(false)
      return
    }

    const selectedEq = equipments.find(eq => eq.unit.code === unitCode)
    setIsDrilling(selectedEq?.unit?.category === "Drilling")
  }, [equipments, unitCode])

  const getStatusColor = useCallback((status?: string) => {
    switch (status) {
      case "POSTED":
        return "green"
      case "LOCKED":
        return "red"
      case "DRAFT":
        return "blue"
      default:
        return "default"
    }
  }, [])

  if (!data) return null

  const attachments = (data.attachments || []) as Array<{ key: string; type: string }>
  const visibleAttachments = attachments.filter(att => {
    if (!isAdmin) return true
    return att.type !== "hm_start_photo" && att.type !== "hm_stop_photo" && att.type !== "operator"
  })

  return (
    <>
      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="Unit & Operation" icon={<ToolOutlined />}>
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Unit Type">{unitType || "-"}</Descriptions.Item>
              <Descriptions.Item label="Unit Code">{unitCode || "-"}</Descriptions.Item>
              <Descriptions.Item label="Vendor Name">{data.vendor_name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Start Date">{data.date_from ? new Date(data.date_from).toLocaleString() : "-"}</Descriptions.Item>
              <Descriptions.Item label="End Date">{data.date_to ? new Date(data.date_to).toLocaleString() : "-"}</Descriptions.Item>
              <Descriptions.Item label="Operator Name">{data.operator_name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Operator ID">{data.operator_id || "-"}</Descriptions.Item>
              {data.type && <Descriptions.Item label="Type">{data.type}</Descriptions.Item>}
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="General Information" icon={<FileTextOutlined />}>
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Shift Type">{data.shift_type || "-"}</Descriptions.Item>
              <Descriptions.Item label="Branch Name">{data.branch_name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Department Name">{data.department_name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Activity Description">{data.activity_desc || data.activity_name || "-"}</Descriptions.Item>
              {data.activity_timesheet && <Descriptions.Item label="Activity Timesheet">{data.activity_timesheet}</Descriptions.Item>}
              {data.activity_type && <Descriptions.Item label="Activity Type">{data.activity_type}</Descriptions.Item>}
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      {hasAssignmentInfo && (
        <Row gutter={24}>
          <Col xs={24}>
            <DetailSection title="Assignment & Location" icon={<EnvironmentOutlined />}>
              <Descriptions column={{ xs: 1, sm: 2 }}>
                {assignmentType && <Descriptions.Item label="Assignment Type">{assignmentType}</Descriptions.Item>}
                {blockCode && <Descriptions.Item label="Block">{blockCode}</Descriptions.Item>}
                {blockDesc && <Descriptions.Item label="Block Description">{blockDesc}</Descriptions.Item>}
                {prospectCode && <Descriptions.Item label="Prospect">{prospectCode}</Descriptions.Item>}
                {data.prospect_desc && <Descriptions.Item label="Prospect Description">{data.prospect_desc}</Descriptions.Item>}
                {locationName && <Descriptions.Item label="Location">{locationName}</Descriptions.Item>}
                {data.location_type && <Descriptions.Item label="Location Type">{data.location_type}</Descriptions.Item>}
                {data.location_timesheet && data.location_timesheet !== locationName && (
                  <Descriptions.Item label="Location Timesheet">{data.location_timesheet}</Descriptions.Item>
                )}
              </Descriptions>
            </DetailSection>
          </Col>
        </Row>
      )}

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="Hours & Metrics" icon={<DashboardOutlined />}>
            <Descriptions column={{ xs: 1, sm: 3 }}>
              <Descriptions.Item label="HM Start">{data.hm_start ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="HM Stop">{data.hm_stop ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Total Hours">{totalHours ?? "-"}</Descriptions.Item>
              {isDrilling && (data.depth_meter_from !== undefined || data.depth_meter_to !== undefined) && (
                <>
                  <Descriptions.Item label="Depth Meter Start">{data.depth_meter_from ?? "-"}</Descriptions.Item>
                  <Descriptions.Item label="Depth Meter End">{data.depth_meter_to ?? "-"}</Descriptions.Item>
                  <Descriptions.Item label="Depth Meter Total Delta">{depthMeterDelta ?? "-"}</Descriptions.Item>
                </>
              )}
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      {visibleAttachments.length > 0 && (
        <Row gutter={24}>
          <Col xs={24}>
            <DetailSection title="Evidence & Photos" icon={<CameraOutlined />}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                {visibleAttachments.map((att, index: number) => {
                  let label = att.type
                  if (att.type === "hm_start_photo") label = "HM Start Photo"
                  if (att.type === "hm_stop_photo") label = "HM Stop Photo"
                  if (att.type === "unit_photo") label = isAdmin ? "Attachment" : "Unit Photo, Including Hull Number"
                  if (att.type === "operator") label = "Operator Selfie"
                  if (att.type === "attachment") label = "Attachment"

                  return <AttachmentItem key={index} fileKey={att.key} label={label} />
                })}
              </div>
            </DetailSection>
          </Col>
        </Row>
      )}

      {hasApprovalInfo && approval && (
        <Row gutter={24}>
          <Col xs={24}>
            <DetailSection title="Approval" icon={<CheckCircleOutlined />}>
              <Descriptions column={{ xs: 1, sm: 2 }}>
                {approval.name && <Descriptions.Item label="Name">{approval.name}</Descriptions.Item>}
                {approval.nik && <Descriptions.Item label="NIK">{approval.nik}</Descriptions.Item>}
                {approval.email && <Descriptions.Item label="Email">{approval.email}</Descriptions.Item>}
              </Descriptions>
            </DetailSection>
          </Col>
        </Row>
      )}

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="Additional Details" icon={<InfoCircleOutlined />}>
            <Descriptions column={1}>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(data.status)}>{data.status || "Unknown"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Note">{data.note || "-"}</Descriptions.Item>
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>
    </>
  )
}

export default HeTimesheetDetailView
